import type { StationStatusSnapshot } from '@metro/station-status'
import type { SuggesterStationStatus } from '@metro/suggester'

/**
 * Adapts the host's live `@metro/station-status` snapshot (naptanId-keyed,
 * `{severity, closed, payload}`) onto the flat `@metro/suggester`
 * `SuggesterStationStatus` shape the Suggester's `cachedStatuses` prop
 * expects (spec §8/§13).
 *
 * TASK-629: `useSuggesterPanel` previously never read `useStationStatuses()`
 * at all, so `<Suggester>` always mounted with `cachedStatuses` undefined —
 * the web mirror of the Android gap where the snapshot reached the station
 * card but was never wired into the suggester. Every idle-section row
 * (Nearby / Favorites / Recents / Popular) and every search-result row was
 * permanently stuck at the Default variant regardless of what the server
 * reported.
 *
 * `closed` is intentionally dropped: the Suggester row model has only two
 * live-status variants (Closed / Advisory — see `SuggestionRow.resolveVariant`),
 * driven purely by `severity`. The richer three-way Closed/Restricted/Advisory
 * split is a StationCard-only distinction (`statusMapping.ts`).
 */
export function toSuggesterCachedStatuses(
  snapshot: StationStatusSnapshot,
): Readonly<Record<string, SuggesterStationStatus>> {
  const result: Record<string, SuggesterStationStatus> = {}
  for (const [naptanId, status] of Object.entries(snapshot)) {
    result[naptanId] = {
      severity: status.severity,
      title: status.payload?.title,
      message: status.payload?.message,
      appearance: status.payload?.appearance,
      fromDate: status.payload?.fromDate,
      toDate: status.payload?.toDate,
      lineIds: status.payload?.lineIds,
    }
  }
  return result
}
